import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './_carousel.scss';

const ControlledCarousel = ({ classNames, children }) => {
  const contentRef = useRef(null);
  const controllerRef = useRef(null);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isControlledScroll, setIsControlledScroll] = useState(false);

  useEffect(() => {
    let scrollingTimeout;

    const handleScrollStart = () => {
      setIsScrolling(true);
      clearTimeout(scrollingTimeout);
    };

    const handleScrollEnd = () => {
      scrollingTimeout = setTimeout(() => {
        setIsScrolling(false);

        if (isControlledScroll) {
          setIsControlledScroll(false);
          return;
        }

        const maxScrollLeft =
          contentRef.current.scrollWidth - contentRef.current.clientWidth;
        const scrollPosition =
          contentRef.current.scrollLeft + contentRef.current.offsetWidth / 2;
        const newIndex = Math.floor(
          (scrollPosition + contentRef.current.offsetWidth / 2) /
            contentRef.current.offsetWidth
        );
        const minScroll = window.innerWidth / 2;

        if (scrollPosition >= maxScrollLeft) {
          setSelectedIndex(contentRef.current.children.length - 1);
          updateCarousels(contentRef.current.children.length - 1);
        } else if (scrollPosition <= minScroll) {
          setSelectedIndex(0);
          updateCarousels(0);
        } else {
          setSelectedIndex(newIndex);
          updateCarousels(newIndex);
        }
      }, 150);
    };

    contentRef.current.addEventListener('scroll', handleScrollStart);
    contentRef.current.addEventListener('scroll', handleScrollEnd);

    return () => {
      contentRef.current.removeEventListener('scroll', handleScrollStart);
      contentRef.current.removeEventListener('scroll', handleScrollEnd);
      clearTimeout(scrollingTimeout);
    };
  }, [isScrolling, isControlledScroll]);

  const updateCarousels = (index) => {
    const itemRect = contentRef.current.children[index].getBoundingClientRect();
    const carouselRect = contentRef.current.getBoundingClientRect();
    const offset =
      itemRect.left -
      carouselRect.left -
      (carouselRect.width - itemRect.width) / 2;

    const controllerItemRect =
      controllerRef.current.children[index].getBoundingClientRect();
    const controllerCarouselRect =
      controllerRef.current.getBoundingClientRect();
    const controllerOffset =
      controllerItemRect.left -
      controllerCarouselRect.left -
      (controllerCarouselRect.width - controllerItemRect.width);

    if (index !== 0) {
      controllerRef.current.scrollBy({
        left: controllerOffset + 50,
        behavior: 'smooth',
      });
      contentRef.current.scrollBy({
        left: offset,
        behavior: 'smooth',
      });
    } else {
      controllerRef.current.scrollBy({
        left: controllerOffset,
        behavior: 'smooth',
      });
      contentRef.current.scrollBy({
        left: offset,
        behavior: 'smooth',
      });
    }
  };

  const handleClick = (index) => {
    setSelectedIndex(index);
    setIsControlledScroll(true);
    updateCarousels(index);
  };

  if (!children) return null;

  const controlled_carousel = (
    <div className="carousel--container">
      <div
        ref={controllerRef}
        className={cn(
          'carousel',
          'carousel--controller-wrapper',
          'carousel--controlled'
        )}
      >
        {children.map((item, index) => (
          <div
            key={index}
            className={cn(
              'carousel--controller-item',
              selectedIndex === index && 'carousel--controller-item--selected'
            )}
            onClick={() => handleClick(index)}
          >
            {item && item.key ? item.key : index}
          </div>
        ))}
      </div>

      <div
        ref={contentRef}
        className={cn(classNames, 'carousel', 'carousel--controlled')}
      >
        {children.map((item, index) => (
          <div
            key={index}
            className={cn(
              'carousel--item',
              selectedIndex === index && 'carousel--item--selected'
            )}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );

  return <>{controlled_carousel}</>;
};

ControlledCarousel.propTypes = {
  classNames: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  pauseScroll: PropTypes.bool,
};

export default ControlledCarousel;
